import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';

import { EIP_7702_REVOCATION_CANCELLED } from '@sorare/wallet-shared';

import { useWalletContext } from '#src/contexts/wallet/index.ts';
import { useTimeoutFn } from '#src/hooks/animations/useTimeoutFn.ts';
import { useMutation } from '#src/hooks/graphql/useMutation.ts';
import { useQuery } from '#src/hooks/graphql/useQuery.ts';

import type {
  RevokeEip7702DelegationMutation,
  RevokeEip7702DelegationMutationVariables,
  Eip7702RevocationsQuery,
  Eip7702RevocationsQueryVariables,
} from './__generated__/useRevokeEip7702Delegation.graphql.ts';

const POLL_INTERVAL_MS = 2000;
const RELAY_TIMEOUT_MS = 120_000;
const CONFIRMATION_ATTEMPTS = 3;
const CONFIRMATION_INTERVAL_MS = 2000;

export type RevokeStatus = 'idle' | 'signing' | 'relaying' | 'done' | 'failed';

const REVOKE_EIP7702_DELEGATION_MUTATION = gql`
  mutation RevokeEip7702DelegationMutation(
    $input: revokeEip7702DelegationInput!
  ) {
    revokeEip7702Delegation(input: $input) {
      revocation {
        id
        status
        transactionHash
        failureReason
      }
      errors {
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  RevokeEip7702DelegationMutation,
  RevokeEip7702DelegationMutationVariables
>;

const EIP7702_REVOCATIONS_QUERY = gql`
  query Eip7702RevocationsQuery {
    currentUser {
      slug
      eip7702Revocations {
        id
        status
        transactionHash
        failureReason
        createdAt
      }
    }
  }
` as TypedDocumentNode<
  Eip7702RevocationsQuery,
  Eip7702RevocationsQueryVariables
>;

const wait = async (ms: number) =>
  new Promise<void>(resolve => {
    setTimeout(resolve, ms);
  });

const confirmDelegationRemoved = async (
  getDelegation: () => Promise<{ delegated: boolean } | undefined>
): Promise<'done' | 'failed'> => {
  let stillDelegated = false;

  for (let attempt = 0; attempt < CONFIRMATION_ATTEMPTS; attempt += 1) {
    if (attempt > 0) {
      await wait(CONFIRMATION_INTERVAL_MS);
    }

    try {
      const delegation = await getDelegation();
      if (delegation?.delegated === false) return 'done';
      if (delegation?.delegated) stillDelegated = true;
    } catch {
      // an unanswered check is retried like a delegated one
    }
  }

  return stillDelegated ? 'failed' : 'done';
};

export const useRevokeEip7702Delegation = () => {
  const { signEip7702Revocation, getEip7702Delegation } = useWalletContext();
  const [rawStatus, setStatus] = useState<RevokeStatus>('idle');
  const [rawFailureReason, setFailureReason] = useState<string>();
  const [revocationId, setRevocationId] = useState<string>();
  const confirming = useRef(false);

  const [revokeDelegation] = useMutation(REVOKE_EIP7702_DELEGATION_MUTATION, {
    showErrorsInForm: true,
  });

  const polling = rawStatus === 'relaying';
  const { data, stopPolling } = useQuery(EIP7702_REVOCATIONS_QUERY, {
    skip: !polling || !revocationId,
    pollInterval: POLL_INTERVAL_MS,
  });

  const revocation = data?.currentUser?.eip7702Revocations.find(
    ({ id }) => id === revocationId
  );
  const revocationStatus = revocation?.status;
  const revocationFailureReason = revocation?.failureReason;

  const failedRemotely = polling && revocationStatus === 'FAILED';
  const status = failedRemotely ? 'failed' : rawStatus;
  const failureReason = failedRemotely
    ? (revocationFailureReason ?? undefined)
    : rawFailureReason;
  const relaying = status === 'relaying';

  useEffect(() => {
    if (failedRemotely) stopPolling();
  }, [failedRemotely, stopPolling]);

  const fail = (reason?: string) => {
    setFailureReason(reason);
    setStatus('failed');
  };

  useTimeoutFn(
    () => {
      if (!confirming.current) fail();
    },
    relaying ? RELAY_TIMEOUT_MS : null
  );

  useEffect(() => {
    if (!relaying) return;

    if (revocationStatus === 'FINALIZED' && !confirming.current) {
      confirming.current = true;
      confirmDelegationRemoved(getEip7702Delegation)
        .then(setStatus)
        .finally(() => {
          confirming.current = false;
        });
    }
  }, [relaying, revocationStatus, getEip7702Delegation]);

  const revoke = async () => {
    setFailureReason(undefined);
    setRevocationId(undefined);
    setStatus('signing');

    let signed;
    try {
      signed = await signEip7702Revocation();
    } catch {
      setStatus('idle');
      return;
    }

    if (signed.error !== undefined) {
      if (signed.error === EIP_7702_REVOCATION_CANCELLED) {
        setStatus('idle');
      } else {
        fail(signed.error);
      }
      return;
    }

    setStatus('relaying');
    const { data: mutationData, errors } = await revokeDelegation({
      variables: {
        input: {
          authorization: signed.authorization,
        },
      },
    });

    const createdId = mutationData?.revokeEip7702Delegation?.revocation?.id;
    if (errors.length > 0 || !createdId) {
      fail(errors[0]?.message);
      return;
    }

    setRevocationId(createdId);
  };

  return { revoke, status, failureReason };
};
